<template>
  <div class="c_image_grid" :data-theme="theme">
    <div class="c_wrapper_2022">
      <div v-if="variant === 'featuredImage'" class="c_image_grid__featured">
        <div class="c_image_grid__featured__content">
          <span>{{ data.grid.length }} {{ data.grid.length > 1 ? $t('Items') : $t('Item') }}</span>
          <h3 v-if="data.title" v-html="data.title"></h3>
          <Button unstyle reset @onClick="openDialog" :aria-label="$t('Open media in popup overlay')">
            {{ $t('View Gallery') }}
          </Button>
        </div>
        <MediaImage background :sources="[featured?.imageFull]" :alt="featured?.alt || featured?.title" />
      </div>
      <div v-if="variant === 'showAll'" class="c_image_grid__show_all">
        <h3 v-if="data.title" v-html="data.title"></h3>
        <div class="c_image_grid__masonry_container">
          <div class="c_image_grid__masonry">
            <Button
              unstyle
              reset
              @onClick="openDialog(index)"
              v-for="(image, index) in data.grid"
              :key="index"
              :aria-label="$t('Open media in popup overlay')"
            >
              <IconSVG handle="zoom" aria-hidden="true" />
              <MediaImage
                :sources="[image?.imageFull]"
                :alt="image?.alt || image?.title"
                :key="`c_image_grid__image--${index}`"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
    <Overlay ref="image_grid_ref" class="c_image_grid__overlay" show-close-button>
      <ImageContentGridSlider :data="data.grid" :active-index-override="activeIndex" />
    </Overlay>
  </div>
</template>

<script>
import { defineComponent, ref } from '@nuxtjs/composition-api';
import Button from 'Components/button/Button.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import ImageContentGridSlider from './ImageContentGridSlider.vue';
import MediaImage from 'Components/image/MediaImage.vue';
import Overlay from 'Components/overlay/Overlay.vue';

export default defineComponent({
  name: 'ImageGrid',
  components: {
    Button,
    IconSVG,
    ImageContentGridSlider,
    MediaImage,
    Overlay,
  },
  props: {
    data: { type: Object, default: () => {} },
  },
  setup(props) {
    const activeIndex = ref(0);

    const variant = props.data?.variant || 'featuredImage';
    const theme = props.data?.theme || 'darkMode';

    const image_grid_ref = ref(null);

    // Set featured to first image
    const featured = props.data?.grid?.[0];

    const openDialog = (index) => {
      if (Number.isInteger(index) && index >= 0 && index <= props.data?.grid?.length) {
        activeIndex.value = index;
      }
      image_grid_ref?.value?.show();
    };

    return { variant, theme, featured, openDialog, image_grid_ref, activeIndex };
  },
});
</script>

<style lang="scss">
.c_image_grid {
  --image-grid-bg: var(--color-black);
  --image-grid-color: var(--color-white);
  --image-grid-link-color: var(--color-yellow);
  &[data-theme='darkMode'] {
    --image-grid-bg: var(--color-black);
    --image-grid-color: var(--color-white);
    --image-grid-link-color: var(--color-yellow);
  }
  &[data-theme='lightMode'] {
    --image-grid-bg: var(--color-white);
    --image-grid-color: var(--color-black);
    --image-grid-link-color: var(--color-red);
  }
  background-color: var(--image-grid-bg);
  color: var(--image-grid-color);
  padding: px_to_rem(24) 0;
  @include mq($mq_sm_to_md) {
    padding: px_to_rem(80) 0;
  }
  &__show_all {
    h3 {
      @include fs_lh(32, 40, 32, 40);
      @include font('graphik_semibold');
      margin-bottom: px_to_rem(14);
      text-align: center;
      @include mq($mq_sm_to_md) {
        margin-bottom: px_to_rem(48);
      }
    }
  }
  &__masonry_container {
    display: flex;
    justify-content: center;
  }

  &__masonry {
    display: flex;
    flex-wrap: wrap;
    max-width: 1150px;
    gap: px_to_rem(12);
    width: 100%;
    button {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-content: center;
      min-width: 144px;
      min-height: 240px;
      max-height: 220px;
      background: var(--image-grid-color);
      flex: 1 0 auto;
      overflow: hidden;
      color: var(--color-white);
      &:after {
        content: '';
        position: absolute;
        inset: 0;
        margin: auto;
        background-color: rgba(255, 255, 255, 0.5);
        height: 100%;
        width: 100%;
        opacity: 0;
        @include transition(all 0.15s ease);
      }
      &:hover {
        @media (hover: hover) and (pointer: fine) {
          cursor: zoom-in;
          img {
            transform: scale(1.05);
          }
          .c_icon_svg {
            opacity: 1;
          }
          &:after {
            opacity: 1;
          }
        }
      }
      figure {
        height: 100%;
      }
      picture {
        height: 100%;
        object-fit: cover;
      }
      img {
        object-fit: cover;
        height: 100%;
        object-position: center;
        transition: transform ease-in-out 0.15s;
        transform: scale(1);
      }
      .c_icon_svg {
        position: absolute;
        inset: 0;
        margin: auto;
        width: px_to_rem(48);
        height: px_to_rem(48);
        z-index: 5;
        background-color: var(--color-black);
        border-radius: px_to_rem(8);
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0;
        svg {
          height: px_to_rem(24);
          width: px_to_rem(24);
        }
      }
    }
    @for $i from 1 through 36 {
      button:nth-child(#{$i}) {
        $h: (random(300) + 70) + px;
        width: $h;
      }
    }
    @media (max-width: 700px) {
      button {
        max-width: 100%;
      }
    }
    @media (min-width: 700px) {
      button {
        max-width: 600px;
      }
    }
  }
  &__featured {
    display: flex;
    position: relative;
    align-items: flex-end;
    justify-content: flex-end;
    overflow: hidden;
    aspect-ratio: 16/10;
    h3 {
      @include fs_lh(24, 32, 18, 28);
      @include font('graphik_semibold');
      margin: px_to_rem(8) 0;
    }
    button {
      color: var(--image-grid-link-color);
      @include font('graphik_semibold');
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    }
    .c_image_bg {
      position: absolute;
    }
    &__content {
      background-color: var(--image-grid-bg);
      color: var(--image-grid-color);
      padding: px_to_rem(24);
      z-index: 10;
      position: relative;
      border-top-left-radius: px_to_rem(16);
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
      text-align: right;
      span {
        display: block;
        @include font('graphik_semibold');
        @include fs_lh(16, 24, 16, 24);
        color: var(--color-gray_112);
      }
      @include mq($mq_md_to_lg) {
        padding: px_to_rem(40);
      }
    }
  }
  &__overlay {
    position: fixed;
    .slide {
      max-width: 100vw;
      height: 100%;
    }
    .c_overlay__mask {
      backdrop-filter: blur(4px);
      background-color: rgba(0, 0, 0, 0.8);
    }
    .c_overlay__content {
      width: 75vw;
    }
  }
}
</style>
